import * as React from "react";

import { getActivityFieldValue } from "Agile/Scripts/Capacity/FieldAggregator";
import { AgileFilterBar, IAgileFilterContext } from "Agile/Scripts/Common/Components/AgileFilterBar/AgileFilterBar";
import { component } from "Agile/Scripts/Common/Components/ComponentRegistration";
import { FilterStatePersistenceManager } from "Agile/Scripts/Common/FilterStatePersistenceManager";
import { HubViewActions } from "Agile/Scripts/Common/HubViewActions";
import { SprintsHubConstants, SprintsHubRoutingConstants } from "Agile/Scripts/Generated/HubConstants";
import * as SprintsHubResources from "Agile/Scripts/Resources/TFS.Resources.SprintsHub";
import * as AgileResources from "Agile/Scripts/Resources/TFS.Resources.Agile";
import { LoadingStatus } from "Agile/Scripts/SprintsHub/Common/CommonContracts";
import { updateFilterState } from "Agile/Scripts/SprintsHub/Common/CommonFunctions";
import { SprintViewRightPanel } from "Agile/Scripts/SprintsHub/Common/SprintViewRightPanel";
import { SprintViewCommonViewActions } from "Agile/Scripts/SprintsHub/SprintView/Components/SprintViewCommonViewActions";
import { SprintViewPivotBase } from "Agile/Scripts/SprintsHub/SprintView/Components/SprintViewPivotBase";
import { ISprintViewPivotContext, ISprintViewRightPanelData, ViewActionKey, RightPanelKey } from "Agile/Scripts/SprintsHub/SprintView/SprintContentViewContracts";
import { TaskboardActions } from "Agile/Scripts/SprintsHub/Taskboard/ActionsCreator/TaskboardActions";
import { TaskboardActionsCreator } from "Agile/Scripts/SprintsHub/Taskboard/ActionsCreator/TaskboardActionsCreator";
import { ITaskboardDetailsPanelOptions, TaskboardWrapper } from "Agile/Scripts/SprintsHub/Taskboard/Components/TaskboardWrapper";
import { ITaskboardViewState, TaskboardSelector } from "Agile/Scripts/SprintsHub/Taskboard/Selectors/TaskboardSelector";
import { SprintsHubTaskboardShortcutActions, TaskboardShortcut } from "Agile/Scripts/SprintsHub/Taskboard/SprintsHubTaskboardShortcuts";
import { TaskboardStore } from "Agile/Scripts/SprintsHub/Taskboard/Store/TaskboardStore";
import { TaskboardGroupBy } from "Agile/Scripts/Taskboard/TaskboardConstants";
import { BacklogConfigurationService } from "Presentation/Scripts/TFS/FeatureRef/BacklogConfiguration/Service";
import { PivotBarActionHelper } from "Presentation/Scripts/TFS/FeatureRef/NewWorkItem";
import { TfsSettingsScopeNames } from "Presentation/Scripts/TFS/Generated/TFS.Server.WebAccess.Constants";
import { delay } from "VSS/Utils/Core";
import { equals } from "VSS/Utils/String";
import { IFilterBarProps } from "VSSUI/FilterBar";
import { IPivotBarAction, IPivotBarViewAction, PivotBarViewActionType, ICommandViewActionProps } from "VSSUI/PivotBar";
import { SplitterElementPosition } from "VSSUI/Splitter";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { ObservableValue } from "VSS/Core/Observable";
import { IViewOptionsValues, VIEW_OPTIONS_CHANGE_EVENT } from "VSSUI/Utilities/ViewOptions";
import { mapToFilterState } from "WorkItemTracking/Scripts/Controls/Filters/WorkItemFilter";
import { isFilterStateEmpty } from "WorkItemTracking/Scripts/Filtering/FilterManager";
import { WorkItem } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { IWorkItemDragInfo } from "Agile/Scripts/Common/IWorkItemDragInfo";
import { DataKeys } from "Agile/Scripts/Common/Agile";
import { LearningBubble } from "Presentation/Scripts/TFS/Components/LearningBubble/LearningBubble";
import { LearningBubbleSettingsKeys, HubViewOptionsTargetSelector } from "Presentation/Scripts/TFS/Components/LearningBubble/Constants";
import { SplitView } from "Agile/Scripts/Common/Components/SplitView/SplitView";
import { PivotBarViewActionArea } from "VSSUI/Components/PivotBar";
import { VssIconType } from "VSSUI/VssIcon";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import { FeatureAvailabilityFlags } from "Presentation/Scripts/TFS/Generated/TFS.Server.WebAccess.Constants";
import { IdentityHelper } from "Presentation/Scripts/TFS/TFS.OM.Identities";
import { format } from "VSS/Utils/String";
import { PeopleSwitcherCallout } from "Agile/Scripts/SprintsHub/Taskboard/Components/PeopleSwitcherCallout";
import * as WorkItemTrackingResources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";
import { LWPComponent } from "VSSPreview/Flux/Components/LWP";

@component("sprint-taskboard-pivot")
export class SprintViewTaskboardPivot extends SprintViewPivotBase<ITaskboardViewState> {
    private _actionsCreator: TaskboardActionsCreator;
    private _store: TaskboardStore;
    private _taskboardRef: TaskboardWrapper;
    private _shortcuts: TaskboardShortcut;
    private _taskboardFilterContext: ObservableValue<IAgileFilterContext>;
    private _peopleSwitcherFilterContext: ObservableValue<IAgileFilterContext>;
    private _initialFilterSet: boolean;
    private _viewOptionsLearningBubble = React.createRef<LearningBubble>();
    private _columnOptionsLearningBubble = React.createRef<LearningBubble>();
    private _peopleSwitcherCalloutTarget: HTMLElement;
    private _iterationFilterStates: IDictionaryStringTo<IFilterState> = {};

    constructor(props: ISprintViewPivotContext) {
        super(props);

        const filterManager = new FilterStatePersistenceManager(TfsSettingsScopeNames.WebTeam, props.team.id, SprintsHubConstants.HUB_NAME, SprintsHubRoutingConstants.TaskboardPivot);
        const peopleSwitcherManager = new FilterStatePersistenceManager(TfsSettingsScopeNames.WebTeam, props.team.id, SprintsHubConstants.HUB_NAME, SprintsHubRoutingConstants.TaskboardPivot, [SprintsHubConstants.PeopleSwitcher], /*suppressTelemetry*/ true);
        const actions = new TaskboardActions();
        this._actionsCreator = new TaskboardActionsCreator(actions, this.props, filterManager, peopleSwitcherManager);
        this._store = new TaskboardStore(actions);

        this.state = TaskboardSelector.getTaskboardViewState(this._store);

        this._store.addChangedListener(this._onStoreChanged);

        this._taskboardFilterContext = new ObservableValue<IAgileFilterContext>({
            filterManager: null,
            skipFocusOnMount: false
        });
        this._peopleSwitcherFilterContext = new ObservableValue<IAgileFilterContext>({
            filterManager: null,
            skipFocusOnMount: false
        });
        props.viewOptions.subscribe(this._onViewOptionsChanged, VIEW_OPTIONS_CHANGE_EVENT);
    }

    public componentWillReceiveProps(nextProps: ISprintViewPivotContext) {
        const { selectedIteration } = this.props;

        if (selectedIteration !== nextProps.selectedIteration) {
            delay(this, 0, () => {
                // When the iteration is changed in the header, this component is being re-rendered as part of an action higher up in the stack
                if (this._actionsCreator) {
                    //  We received new props from switching sprints or due to iteration date or name changes.
                    //  Reload the taskboard and display a loading dialog while we get data from the server.
                    this._actionsCreator.reloadTaskboardData(nextProps);
                }
            });
        }
    }

    public componentWillUpdate(nextProps: ISprintViewPivotContext, nextState: ITaskboardViewState) {
        super.componentWillUpdate(nextProps, nextState);

        if (!this._initialFilterSet && nextState.initialFilterState) {
            nextProps.filter.setState(nextState.initialFilterState);
            this._initialFilterSet = true;

            if (nextProps.filter.hasChangesToReset()) {
                delay(this, 0, () => {
                    this._showFilter(true /* skipFocus */);
                });
            }
        }
    }

    public componentWillMount() {
        super.componentWillMount();
        this._shortcuts = this._setupTaskboardHubShortcuts();
        this._actionsCreator.fetchTaskboardData();
    }

    public componentDidUpdate(prevProps: ISprintViewPivotContext, prevState: ITaskboardViewState) {
        super.componentDidUpdate(prevProps, prevState);
        const { filter, selectedIteration } = this.props;

        this._shortcuts = this._setupTaskboardHubShortcuts();

        if (this.state.rightPanelId !== prevState.rightPanelId) {
            this._taskboardRef.resize();
        }

        if (filter && !equals(prevProps.selectedIteration.id, selectedIteration.id, true)) {
            updateFilterState(this._iterationFilterStates, prevProps.selectedIteration.id, selectedIteration.id, filter, (state: IFilterState) => this._actionsCreator.saveFilterState(state));
            if (filter.hasChangesToReset()) {
                delay(this, 0, () => {
                    this._showFilter(true /* skipFocus */);
                });
            }
        }
    }

    public componentDidMount() {
        super.componentDidMount();

        const {
            contributionId
        } = this.getSplitterState();

        this.props.viewOptions.setViewOption(ViewActionKey.RIGHT_PANEL, contributionId || RightPanelKey.OFF);

        if (!this._initialFilterSet && this.state.initialFilterState) {
            this.props.filter.setState(this.state.initialFilterState);
            this._initialFilterSet = true;

            if (this.props.filter.hasChangesToReset()) {
                delay(this, 0, () => {
                    this._showFilter(true /* skipFocus */);
                });
            }
        }
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this.props.viewOptions.unsubscribe(this._onViewOptionsChanged, VIEW_OPTIONS_CHANGE_EVENT);
        this._store.removeChangedListener(this._onStoreChanged);
        this._shortcuts.unregisterAllShortcuts();
    }

    public getCommands(props: ISprintViewPivotContext, state: ITaskboardViewState): IPivotBarAction[] {
        const addNewItemCommand = PivotBarActionHelper.getNewWorkItemPivotBarAction({
            addNewItem: this._addNewItem,
            workItemTypesNames: state.newWorkItemTypes,
            disabled: state.isNewWorkItemButtonDisabled
        });

        const commands = [
            addNewItemCommand
        ];

        if (!FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.WebAccessAgileTaskboardDisableCustomColumns)) {
            commands.push({
                key: "taskboard-column-options",
                disabled: false,
                name: WorkItemTrackingResources.ColumnOptionsTitle,
                iconProps: {
                    iconName: "Repair"
                },
                important: true,
                onClick: this._showColumnOptions,
                title: WorkItemTrackingResources.ColumnOptionsTitle
            });
        }
        return commands;
    }

    private _showColumnOptions = () => {
        this.props.stopLearningBubble();
        if (this._columnOptionsLearningBubble.current) {
            this._columnOptionsLearningBubble.current.cancelDelayedShow();
        }
        this._actionsCreator.toggleShowColumnOptions(true);
    }

    private _hideColumnOptions = () => {
        this.props.showLearningBubble();
        this._actionsCreator.toggleShowColumnOptions(false);
    }

    public getViewActions(props: ISprintViewPivotContext, state: ITaskboardViewState): IPivotBarViewAction[] {
        const areViewActionsDisabled = state.loadingStatus === LoadingStatus.Loading;
        const commonViewActions = SprintViewCommonViewActions.getViewActions(props, areViewActionsDisabled, true /* include planning */, state.rightPanelId);

        commonViewActions.push(HubViewActions.getFilterAction(this._toggleFilterBar, state.isFiltered));

        if (state.loadingStatus !== LoadingStatus.LoadedWithContent) {
            // Don't set the groupBy view action as it cannot be disabled. If there is no taskboard data
            // this view action does not make sense to render.
            return commonViewActions;
        } else {
            if (!this.props.viewOptions.getViewOption(ViewActionKey.GROUP_BY_KEY)) {
                // Set the group by action and set the current state if not set yet
                this.props.viewOptions.setViewOption(ViewActionKey.GROUP_BY_KEY, state.groupBy);
            }
            if (FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.WebAccessAgileTaskboardPeopleSwitcher, false)) {
                // inserting at the 1st index, after the sprint picker 
                commonViewActions.splice(1, 0, this._getPeopleSwitcherViewActions(this._onPeopleSwitcherClicked, state.peopleSwitcherFilterValue));
            }
            return [
                SprintViewCommonViewActions.getGroupBy(state.parentPluralName),
                ...commonViewActions
            ];
        }
    }

    public isDataReady(): boolean {
        return this.state.loadingStatus === LoadingStatus.LoadedWithContent ||
            this.state.loadingStatus === LoadingStatus.LoadedNoContent ||
            this.state.loadingStatus === LoadingStatus.ErrorLoadingData;
    }

    public render(): JSX.Element {
        const {
            rightPanelId
        } = this.state;
        return (
            <SplitView
                className="sprint-view-splitter"
                onRenderNearElement={this._renderMainContent}
                onRenderFarElement={rightPanelId && rightPanelId !== RightPanelKey.OFF && this._renderRightPanel}
                registrySettingsPrefix="Agile/SprintsHub/Pivots/TaskboardSplitView"
                fixedElement={SplitterElementPosition.Far}
            />
        );
    }

    private _renderMainContent = (): JSX.Element => {
        const {
            filter,
            onScheduleWork,
            selectedIteration,
            setFocusOnHub,
            team,
            selectedIterationTimeframe,
        } = this.props;

        const {
            aggregatedCapacityData,
            groupBy,
            loadingStatus,
            taskboardData,
            exceptionsInfo,
            newWorkItemTypes,
            peopleSwitcherFilter
        } = this.state;
        const backlogConfiguration = BacklogConfigurationService.getBacklogConfiguration().requirementBacklog;
        let defaultWorkItemType = backlogConfiguration.defaultWorkItemType;
        const defaultLowerCase = defaultWorkItemType.toLocaleLowerCase();
        if (!newWorkItemTypes.some(workItemType => workItemType.toLocaleLowerCase() === defaultLowerCase)) {
            defaultWorkItemType = newWorkItemTypes[0];
        }

        const columnOptions = this._renderColumnOptions();
        return (
            <div className="taskboard-wrapper" role="main" aria-label={SprintsHubResources.TaskboardPivot}>
                <LearningBubble
                    ref={this._viewOptionsLearningBubble}
                    settingsKey={LearningBubbleSettingsKeys.RightPanelViewOptions}
                    target={HubViewOptionsTargetSelector}
                    text={AgileResources.PaneClosedLearningBubbleInfoText}
                    buttonLabel={AgileResources.PaneClosedLearningBubbleButtonText} />
                {this._renderAgileFilter()}
                {this._renderPeopleSwitcherCallout()}
                {this._renderColumnOptionsLearningBubble()}
                <TaskboardWrapper
                    ref={this._resolveTaskboardWrapperRef}
                    groupBy={groupBy}
                    iterationPath={selectedIteration.iterationPath}
                    onScheduleWorkClicked={onScheduleWork}
                    status={loadingStatus}
                    setNewWorkItemDisabledState={this._setNewWorkItemButtonState}
                    taskboard={taskboardData}
                    exceptionsInfo={exceptionsInfo}
                    refreshTaskboard={this._refreshTaskboard}
                    onNewParentDiscarded={setFocusOnHub}
                    aggregatedCapacityData={aggregatedCapacityData}
                    onDetailsPanelOptionsUpdated={this._handleDetailsPanelOptionsUpdated}
                    lookupFieldValue={this._lookupFieldValue}
                    teamId={team.id}
                    taskboardFilterContext={this._taskboardFilterContext}
                    peopleSwitcherFilterContext={this._peopleSwitcherFilterContext}
                    filter={filter}
                    peopleSwitcherFilter={peopleSwitcherFilter}
                    defaultWorkItemType={defaultWorkItemType}
                    newWorkItemButtonDisabled={this.state.isNewWorkItemButtonDisabled}
                    selectedIterationTimeframe={selectedIterationTimeframe}
                />
                {columnOptions}
            </div>
        );
    }

    private _renderColumnOptions = () => {
        if (!this.state.showColumnOptions) {
            return null;
        }

        const { taskWorkItemTypesToStateMap, columnIdToWorkItemTypeStateMap, states } = this.state.taskboardData.taskboardModel;
        const columns = [...this.state.taskboardData.taskboardModel.columns];
        const witKeyNameMap = {};
        const stateKeyNameMap = {};

        const normalizedTaskWorkItemTypesToStateMap = {};
        const normalizedColumnIdToWorkItemTypeStateMap = {}

        // normalize taskWorkItemTypesToStateMap
        const taskWorkItemTypes = Object.keys(taskWorkItemTypesToStateMap);
        for (const workItemType of taskWorkItemTypes) {
            const witKey = workItemType.toLocaleLowerCase();
            witKeyNameMap[witKey] = workItemType;
            normalizedTaskWorkItemTypesToStateMap[witKey] = {};

            for (const stateName of Object.keys(taskWorkItemTypesToStateMap[workItemType])) {
                const stateKey = stateName.toLocaleLowerCase();
                stateKeyNameMap[stateKey] = stateName;
                normalizedTaskWorkItemTypesToStateMap[witKey][stateKey] = taskWorkItemTypesToStateMap[workItemType][stateName];
            }
        }

        // If there are no columns then provide the default mapping
        if (columns.length === 0) {
            const taskWorkItemTypes = Object.keys(normalizedTaskWorkItemTypesToStateMap);
            states.forEach((state, index) => {
                const stateKey = state.toLocaleLowerCase();
                // Create a column for the state
                const column = {
                    id: state.toLocaleLowerCase(),
                    name: state,
                    order: index,
                    isNew: true

                };
                normalizedColumnIdToWorkItemTypeStateMap[column.id] = {};
                // Map states for each work item type to the column
                for (const witKey of taskWorkItemTypes) {
                    if (normalizedTaskWorkItemTypesToStateMap[witKey][stateKey]) {
                        normalizedColumnIdToWorkItemTypeStateMap[column.id][witKey] = stateKey;
                    }
                }

                columns.push(column);
            });
        } else {
            // normalize columnIdToWorkItemTypeStateMap
            for (const columnId of Object.keys(columnIdToWorkItemTypeStateMap)) {
                normalizedColumnIdToWorkItemTypeStateMap[columnId] = {};
                for (const workItemTypeName of Object.keys(columnIdToWorkItemTypeStateMap[columnId])) {
                    const witKey = workItemTypeName.toLocaleLowerCase();
                    const state = columnIdToWorkItemTypeStateMap[columnId][workItemTypeName];
                    const stateKey = state.toLocaleLowerCase();

                    witKeyNameMap[witKey] = witKeyNameMap[witKey] || workItemTypeName;
                    stateKeyNameMap[stateKey] = stateKeyNameMap[stateKey] || state;
                    normalizedColumnIdToWorkItemTypeStateMap[columnId][witKey] = normalizedColumnIdToWorkItemTypeStateMap[columnId][witKey] || stateKey;
                }
            }
        }

        return (
            <LWPComponent
                wrappedType={"taskboard-custom-column-panel"}
                dependencies={["ms.vss-work-web.agile-taskboard-custom-column-panel"]}
                columns={columns}
                normalizedTaskWorkItemTypesToStateMap={normalizedTaskWorkItemTypesToStateMap}
                normalizedColumnIdToWorkItemTypeStateMap={normalizedColumnIdToWorkItemTypeStateMap}
                witKeyNameMap={witKeyNameMap}
                stateKeyNameMap={stateKeyNameMap}
                teamId={this.props.team.id}
                onClose={this._hideColumnOptions}
            />
        )
    }

    private _renderRightPanel = (): JSX.Element => {
        const {
            sprintCapacityOptions,
            loadingStatus,
            teamCapacityModel,
            fieldAggregator,
            exceptionsInfo,
            droppableOptions,
            rightPanelId
        } = this.state;

        const panelData: ISprintViewRightPanelData = {
            loading: loadingStatus === LoadingStatus.Loading || loadingStatus === LoadingStatus.None,
            exceptionsInfo: exceptionsInfo,
            selectedContributionId: rightPanelId,
            eventHelper: null,
            getSelectedWorkItems: null,
            workDetailsData: {
                capacityOptions: sprintCapacityOptions,
                teamCapacityModel,
                fieldAggregator,
                capacityActions: null, // Taskboard doesn't trigger actions that update team members capacity.
                droppableWorkItemChangeOptions: droppableOptions
            }
        };

        return (
            <SprintViewRightPanel
                selectedIteration={this.props.selectedIteration}
                onDismiss={this._dismissRightPane}
                panelData={panelData}
                team={this.props.team}
                getWorkItemDragInfo={this._getWorkItemDraggingInformation}
                moveWorkItemsToIteration={this._moveWorkItemsToIteration}
            />
        );
    }

    private _renderAgileFilter = (): React.ReactElement<IFilterBarProps> => {
        if (this.state.isFilterBarOpen) {
            return (
                <AgileFilterBar
                    filter={this.props.filter}
                    filterContext={this._taskboardFilterContext}
                    onFilterChanged={this._onFilterChanged}
                    onDismissClicked={this._onFilterDismissClicked}
                />
            );
        }
    }

    private _renderPeopleSwitcherCallout(): JSX.Element {
        const { isPeopleSwitcherCalloutOpen, peopleSwitcherFilter } = this.state;
        if (isPeopleSwitcherCalloutOpen) {
            return (
                <PeopleSwitcherCallout
                    calloutTarget={this._peopleSwitcherCalloutTarget}
                    onSelectionChanged={this._onPeopleSwitcherSelectionChanged}
                    onDismiss={this._onPeopleSwitcherDismissed}
                    filter={peopleSwitcherFilter}
                    filterContext={this._peopleSwitcherFilterContext}
                />
            );
        }
        return null;
    }

    private _renderColumnOptionsLearningBubble(): JSX.Element {
        if (!FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.WebAccessAgileTaskboardDisableCustomColumns)) {
            return (
                <LearningBubble
                    ref={(bubble) => this.props.saveLearningBubbleRef(bubble)}
                    settingsKey={LearningBubbleSettingsKeys.TaskboardColumOptions}
                    target={"[data-command-key='taskboard-column-options']"}
                    text={SprintsHubResources.ColumnOptionsLearningBubbleText}
                    buttonLabel={AgileResources.PaneClosedLearningBubbleButtonText}/>
            );
        }
        else {
            return null;
        }
    }

    private _onFilterDismissClicked = (): void => {
        this._toggleFilterBar();
    }

    protected shouldUpdateCommandsOnUpdate(nextProps: ISprintViewPivotContext, nextState: ITaskboardViewState): boolean {
        return nextProps.nextIteration !== this.props.nextIteration ||
            nextState.newWorkItemTypes !== this.state.newWorkItemTypes ||
            nextState.groupBy !== this.state.groupBy ||
            nextState.isNewWorkItemButtonDisabled !== this.state.isNewWorkItemButtonDisabled;
    }

    protected shouldUpdateViewActionsOnUpdate(nextProps: ISprintViewPivotContext, nextState: ITaskboardViewState): boolean {
        return nextState.groupBy !== this.state.groupBy ||
            nextState.parentPluralName !== this.state.parentPluralName ||
            nextState.loadingStatus !== this.state.loadingStatus ||
            this.props.selectedIteration !== nextProps.selectedIteration ||
            this.state.peopleSwitcherFilterValue !== nextState.peopleSwitcherFilterValue ||
            this.state.isFiltered !== nextState.isFiltered ||
            this.state.isFilterBarOpen !== nextState.isFilterBarOpen ||
            this.state.rightPanelId !== nextState.rightPanelId;
    }

    protected shouldResizeFilterBarOnUpdate(nextState: ITaskboardViewState): boolean {
        return this.state.isFilterBarOpen &&
            ((this.state.rightPanelId === RightPanelKey.OFF &&
                nextState.rightPanelId !== RightPanelKey.OFF) ||
                (this.state.rightPanelId !== RightPanelKey.OFF &&
                    nextState.rightPanelId === RightPanelKey.OFF))
    }

    protected _onViewOptionsChanged = (value: IViewOptionsValues, action?: string): void => {
        if (this.state.groupBy && value[ViewActionKey.GROUP_BY_KEY] && this.state.groupBy !== value[ViewActionKey.GROUP_BY_KEY]) {

            const groupByString = value[ViewActionKey.GROUP_BY_KEY] as TaskboardGroupBy;
            this._actionsCreator.groupByChanged(groupByString);
        }

        if (value.hasOwnProperty(ViewActionKey.RIGHT_PANEL)) { // If a contribution was toggled
            const newValue: string = value[ViewActionKey.RIGHT_PANEL];
            this.updateSplitterState(newValue);
            this._actionsCreator.setRightPanelId(newValue);
        }
    }

    private _toggleFilterBar = (): void => {
        this._actionsCreator.toggleFilterBar(!this.state.isFilterBarOpen);
    }

    private _onFilterChanged = (filterState: IFilterState): void => {
        this._actionsCreator.saveFilterState(filterState);

        if (isFilterStateEmpty(mapToFilterState(filterState))) {
            this._actionsCreator.toggleFiltered(false);
        } else {
            this._actionsCreator.toggleFiltered(true);
        }
    }

    private _addNewItem = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item: IPivotBarAction, workItemTypeName: string) => {
        if (this._taskboardRef) {
            this._taskboardRef.newItemRequested(workItemTypeName);
        }
    }

    private _setNewWorkItemButtonState = (disabled: boolean) => {
        this._actionsCreator.setNewWorkItemButtonState(disabled);
    }

    private _resolveTaskboardWrapperRef = (element: TaskboardWrapper) => {
        this._taskboardRef = element;
    }

    private _refreshTaskboard = () => {
        this._actionsCreator.reloadTaskboardData();
    }

    private _onStoreChanged = () => {
        this.setState(TaskboardSelector.getTaskboardViewState(this._store));
    }

    private _handleDetailsPanelOptionsUpdated = (options: ITaskboardDetailsPanelOptions): void => {
        if (!options) {
            return;
        }

        delay(this, 0, () => {
            this._actionsCreator.initializeRightPanelContext(options);
        });
    }

    private _lookupActivityFieldValue = (workItem: WorkItem): string => {
        const { sprintCapacityOptions, teamCapacityModel } = this.state;
        if (sprintCapacityOptions && teamCapacityModel) {
            return getActivityFieldValue(
                sprintCapacityOptions.activityFieldDisplayName,
                sprintCapacityOptions.allowedActivities,
                workItem,
                (assignedTo: string) => teamCapacityModel.getTeamMemberCapacity(assignedTo)
            );
        }

        return null;
    }

    private _lookupFieldValue = (workItem: WorkItem, fieldName: string): any => {
        let value = null;
        if (workItem && fieldName) {
            const { sprintCapacityOptions } = this.state;
            if (sprintCapacityOptions && equals(fieldName, sprintCapacityOptions.activityFieldReferenceName, true)) {
                value = this._lookupActivityFieldValue(workItem);
            } else { // try fetching it from the workItem
                value = workItem.getFieldValue(fieldName);
            }
        }

        return value;
    }

    /**
     * Sets up the hub shortcuts for the taskboard.  This is 'n' for add new item because
     * we must interact with the hub commands. Other shortcuts are setup in TaskBoardView.
     */
    private _setupTaskboardHubShortcuts() {
        const actions: SprintsHubTaskboardShortcutActions = {
            newItem: this._triggerAddNewItem,
            activateFilter: () => this._showFilter()
        };

        return new TaskboardShortcut(actions);
    }

    private _triggerAddNewItem = () => {
        // This will default to first when there are multiple work items on the backlog.
        // Waiting on US 1148097 to put focus on add button.
        this._addNewItem(null, null, this.state.newWorkItemTypes[0]);
    }

    private _showFilter = (skipFocus?: boolean): void => {
        if (skipFocus && this._taskboardFilterContext) {
            this._taskboardFilterContext.value.skipFocusOnMount = true;
        }

        this._actionsCreator.toggleFilterBar(true);
    }

    private _dismissRightPane = () => {
        this.props.viewOptions.setViewOption(ViewActionKey.RIGHT_PANEL, RightPanelKey.OFF);
        if (this._viewOptionsLearningBubble.current) {
            this._viewOptionsLearningBubble.current.showIfNeeded();
        }
    }

    private _getWorkItemDraggingInformation = ($tile: JQuery): IWorkItemDragInfo => {
        const workItemId: number = $tile.data(DataKeys.DataKeyId);
        const workItemType: string = $tile.data(DataKeys.DataKeyType);

        return {
            areAllItemsOwned: true,
            selectedWorkItemTypes: [workItemType],
            selectedWorkItemIds: [workItemId],
            topLevelWorkItemIds: [],
            topLevelWorkItemTypes: []
        }
    }

    private _moveWorkItemsToIteration = (workItemIds: number[], newIterationPath: string, $tile: JQuery): void => {
        this._taskboardRef.moveWorkItemToIteration(workItemIds[0], newIterationPath, $tile);
    }

    private _getPeopleSwitcherViewActions = (onClick: (event: React.MouseEvent<HTMLElement>) => void, peopleSwitcherFilterValue: string): IPivotBarViewAction => {
        const actionProps: ICommandViewActionProps = {
            className: "people-switcher-view-action",
            showChevron: true
        };
        const action = {
            actionType: PivotBarViewActionType.Command,
            important: true,
            key: "people-switcher",
            name: format(SprintsHubResources.PersonSelected, this._getPeopleSwitcherDisplayValue(peopleSwitcherFilterValue)),
            actionProps,
            iconProps: { iconName: "People", iconType: VssIconType.fabric },
            onClick,
            viewActionRenderArea: PivotBarViewActionArea.beforeViewOptions
        };
        return action;
    }

    private _getPeopleSwitcherDisplayValue = (peopleSwitcherFilterValue: string): string => {
        const entity = IdentityHelper.parseUniquefiedIdentityName(peopleSwitcherFilterValue);
        return entity.displayName;
    }

    private _onPeopleSwitcherClicked = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this._peopleSwitcherCalloutTarget = ev.currentTarget;
        this._actionsCreator.togglePeopleSwitcherCallout();
    }

    private _onPeopleSwitcherSelectionChanged = (filterState: IFilterState) => {
        this._peopleSwitcherCalloutTarget.click();
        this._actionsCreator.handlePeopleSwitcherUpdate(filterState);
    }

    private _onPeopleSwitcherDismissed = () => {
        this._peopleSwitcherCalloutTarget.click();
    }
}
